import { type ReactElement, type ReactNode, useId } from "react";
import { Box, FormControl, FormHelperText, Typography } from "@mui/material";

import { styled } from "@mui/material/styles";
import {
  type Control,
  type FieldValues,
  type Path,
  useController,
} from "react-hook-form";
import PhoneInputComponent, {
  PhoneInputProps as InputProps,
} from "react-phone-input-2";

const BoxStyled = styled(Box)(({ theme }) => ({
  "& > .react-tel-input input": {
    height: 45,
    border: `1px solid ${theme.vars.palette.grey.A400}`,
    width: "100%",
  },
  "& > .react-tel-input :disabled": {
    background: theme.vars.palette.grey.A400,
  },
  "& > .react-tel-input .flag-dropdown": {
    border: `1px solid ${theme.vars.palette.grey.A400}`,
  },
}));

type PhoneInputProps<T extends FieldValues> = {
  fieldName: Path<T>;
  label?: ReactNode;
  hideLabel?: boolean;
  hideError?: boolean;
  control: Control<T>;
  fullWidth?: boolean;
  hide?: boolean;
} & InputProps;

export const PhoneInput = <T extends FieldValues>({
  fieldName,
  label,
  hideLabel = false,
  hideError = false,
  fullWidth = true,
  control,
  hide,
  disabled,
  placeholder,
  ...inputProps
}: PhoneInputProps<T>): ReactElement | null => {
  const id = useId();

  const {
    field: { ...field },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });

  if (hide) {
    return null;
  }

  return (
    <>
      {!hideLabel && <Typography id={id}>{label}</Typography>}
      <FormControl fullWidth={fullWidth} error={!!error} size={"small"}>
        <BoxStyled>
          <PhoneInputComponent
            country="no"
            prefix="+"
            {...inputProps}
            {...field}
            value={field.value || ""}
            specialLabel={""}
            inputProps={{
              "aria-labelledby": hideLabel ? undefined : id,
              "aria-label": hideLabel ? label : undefined,
            }}
          />
        </BoxStyled>
        {!hideError && error?.message && (
          <FormHelperText>{error.message}</FormHelperText>
        )}
      </FormControl>
    </>
  );
};
